/**
 * Pay Later offline queue. Separate from the catalog cache (bhima-pos-catalog).
 * Cash / UPI / Card charges are never stored here.
 *
 * Replay is oldest first and does not skip a blocked row. A settle waits until
 * its charge has a server order_id. A stale error after a send does not prove
 * the id was never stored — the 48-hour check currently runs before the lookup.
 */
import { useEffect, useState } from 'react';
import { createPosSale, mapPosChargeError } from '../api/pos';
import { mapPayLaterError, newClientPaymentId, settlePayLater } from '../api/payLaters';
import { payableForTender, phoneDigits } from './posMoney';
import { formatWeightKg, lineAmount, roundMoney } from './soldBy';

const DB_NAME = 'bhima-pos-offline';
const DB_VERSION = 1;
const OPS = 'ops';
const PHONES = 'phones';
const META = 'meta';

const COUPON_ERRORS = new Set([
  'coupon_not_found',
  'coupon_not_started',
  'coupon_expired',
  'min_order_not_met',
  'coupon_exhausted',
  'coupon_user_limit',
]);

/** Failures that delete CLIENTSALE# so the same id can be retried. */
export const POINTER_CLEARED_ERRORS = new Set([
  'lot_id_required',
  'lot_mismatch',
  'qty_step_mismatch',
  'below_min_order_qty',
  'above_max_order_qty',
  'item_unavailable',
  'insufficient_stock',
  ...COUPON_ERRORS,
]);

/** Checked before a pointer is written. Stale is ambiguous once a POST was sent. */
const PRE_POINTER_ERRORS = new Set([
  'invalid_client_sale_id',
  'invalid_charged_at',
  'charged_at_in_future',
  'stale_charge',
  'customer_name_required',
  'customer_phone_required',
  'invalid_phone',
  'address_required',
  'address_line1_required',
  'address_line2_required',
  'invalid_address_line1',
  'invalid_address_line2',
  'block_required',
  'flat_required',
]);

const STALE_AMBIGUOUS = new Set([
  'stale_charge',
  'stale_settle',
  'charged_at_in_future',
  'settled_at_in_future',
  'invalid_charged_at',
  'invalid_settled_at',
]);

const listeners = new Set();

export function subscribeOfflineQueue(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emitQueue() {
  listeners.forEach((listener) => {
    try { listener(); } catch { /* UI refresh is best-effort */ }
  });
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openOfflineDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OPS)) {
        const ops = db.createObjectStore(OPS, { keyPath: 'id' });
        ops.createIndex('queuedAt', 'queuedAt');
      }
      if (!db.objectStoreNames.contains(PHONES)) {
        db.createObjectStore(PHONES, { keyPath: 'phone' });
      }
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function createNativeOfflineStorage() {
  let dbPromise;
  const database = () => {
    if (!dbPromise) dbPromise = openOfflineDb();
    return dbPromise;
  };
  return {
    async getAll(store) {
      const db = await database();
      const tx = db.transaction(store, 'readonly');
      return requestToPromise(tx.objectStore(store).getAll());
    },
    async get(store, key) {
      const db = await database();
      const tx = db.transaction(store, 'readonly');
      const row = await requestToPromise(tx.objectStore(store).get(key));
      return row || null;
    },
    async put(store, record) {
      const db = await database();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.objectStore(store).put(record);
      });
    },
    async delete(store, key) {
      const db = await database();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.objectStore(store).delete(key);
      });
    },
  };
}

function estimateTotal(charge) {
  const merchandise = (charge?.items || []).reduce(
    (sum, line) => sum + lineAmount(line.price, line.qty),
    0,
  );
  return roundMoney(Math.max(0, merchandise - (Number(charge?.discountAmount) || 0)));
}

export function isCreditCharge(row) {
  return String(row?.paymentMethod || 'PAY_LATER').toUpperCase() === 'PAY_LATER';
}

/** Amount the cashier took. Cash is nearest ₹1. UPI, Card, and Pay Later keep paise. */
export function queuedBillAmount(charge) {
  const estimate = estimateTotal(charge);
  const method = String(charge?.paymentMethod || 'PAY_LATER').toUpperCase();
  if (method === 'CASH') {
    const stored = Number(charge?.drawerAmount);
    if (Number.isFinite(stored) && stored > 0) return roundMoney(stored);
    return payableForTender(estimate, 'CASH').payable;
  }
  return estimate;
}

/** Pieces already on waiting bills. Same lot only when this line has a lot. */
export function reservedQty(charges, itemId, lotId) {
  const id = String(itemId || '');
  if (!id) return 0;
  let total = 0;
  for (const row of charges || []) {
    if (!row || row.orderId || row.conflict) continue;
    if (row.type && row.type !== 'charge') continue;
    for (const line of row.items || []) {
      if (String(line.item_id || '') !== id) continue;
      if (lotId && line.lot_id && String(line.lot_id) !== String(lotId)) continue;
      const qty = Number(line.qty);
      if (Number.isFinite(qty) && qty > 0) total += qty;
    }
  }
  return total;
}

export function lineStockIssue(line, charges) {
  if (!line || line.catalogComplete === false) return '';
  const stock = Number(line.stock);
  if (!Number.isFinite(stock)) return '';
  const left = stock - reservedQty(charges, line.id, line.lotId);
  if (Number(line.qty) <= left) return '';
  const byWeight = String(line.soldBy || '').toUpperCase() === 'WEIGHT';
  if (left > 0) {
    return byWeight
      ? `Only ${formatWeightKg(left, line.unit || 'kg')} of ${line.name} left`
      : `Only ${left} of ${line.name} left`;
  }
  return `${line.name} is out of stock${line.lotId ? ' on this lot' : ''}`;
}

export function cartStockIssue(cart, charges) {
  for (const line of cart || []) {
    const message = lineStockIssue(line, charges);
    if (message) return message;
  }
  return '';
}

export function localReference(clientSaleId) {
  return String(clientSaleId || '').replace(/-/g, '').slice(0, 8).toUpperCase();
}

export function chargeLinesMatch(queuedItems, receiptLines) {
  const keyOf = (line, idKey) => {
    const id = String(line?.[idKey] || line?.item_id || line?.id || '');
    const qty = roundMoney(line?.qty);
    const lot = String(line?.lot_id || line?.lotId || '');
    return `${id}|${qty}|${lot}`;
  };
  const left = (queuedItems || []).map((line) => keyOf(line, 'item_id')).sort();
  const right = (receiptLines || []).map((line) => keyOf(line, 'item_id')).sort();
  if (left.length !== right.length) return false;
  return left.every((key, index) => key === right[index]);
}

export function canDiscardCharge(row) {
  if (!row || row.type !== 'charge') return false;
  if (row.orderId || row.conflict) return false;
  if (row.errorCode === 'sale_in_progress') return false;
  if (!row.attempted) return true;
  if (STALE_AMBIGUOUS.has(row.errorCode)) return false;
  if (row.errorCode === 'network' || row.errorCode === 'unauthorized' || row.errorCode === 'forbidden') {
    return false;
  }
  if (POINTER_CLEARED_ERRORS.has(row.errorCode)) return true;
  if (PRE_POINTER_ERRORS.has(row.errorCode)) return true;
  return false;
}

export function canEditCharge(row) {
  if (!canDiscardCharge(row)) return false;
  if (!row.attempted) return true;
  return POINTER_CLEARED_ERRORS.has(row.errorCode) || PRE_POINTER_ERRORS.has(row.errorCode);
}

export function buildProvisionalReceipt(charge, { cashierName } = {}) {
  const items = charge?.items || [];
  const method = String(charge?.paymentMethod || 'PAY_LATER').toUpperCase();
  const payLater = method === 'PAY_LATER';
  const subtotal = roundMoney(items.reduce((sum, line) => sum + lineAmount(line.price, line.qty), 0));
  const discount = payLater ? roundMoney(charge?.discountAmount || 0) : 0;
  const net = roundMoney(Math.max(0, subtotal - discount));
  const quote = payLater
    ? { payable: net, round_off: 0 }
    : payableForTender(net, method);
  const phone = phoneDigits(charge?.customerPhone);
  const needsLot = Boolean(charge?.needsLot)
    || items.some((line) => line.requiresLot && !line.lot_id);
  const note = needsLot
    ? 'Provisional — bill number after sync. Lot will be picked when this bill syncs.'
    : 'Provisional — bill number after sync.';
  return {
    provisional: true,
    payment_method: method,
    payment_status: payLater ? 'PENDING' : 'COLLECTED',
    display_id: localReference(charge?.clientSaleId),
    customer_number: charge?.customerNumber || null,
    client_sale_id: charge?.clientSaleId,
    created_at: charge?.chargedAt || new Date().toISOString(),
    counter: charge?.counter || '',
    cashier_name: cashierName || charge?.cashierName || '',
    customer_name: payLater ? (charge?.customerName || '') : '',
    customer_phone: phone ? `+91${phone}` : null,
    address_line1: payLater ? (charge?.addressLine1 || '') : '',
    address_line2: payLater ? (charge?.addressLine2 || '') : '',
    block: payLater ? (charge?.block || '') : '',
    flat: payLater ? (charge?.flat || '') : '',
    line_items: items.map((line) => ({
      item_id: line.item_id,
      name: line.name || line.item_id,
      qty: line.qty,
      sold_by: line.soldBy,
      unit: line.unit,
      unit_price: line.price,
      line_total: lineAmount(line.price, line.qty),
    })),
    subtotal,
    discount_amount: discount,
    coupon_code: payLater ? (charge?.couponCode || null) : null,
    round_off: quote.round_off,
    total: quote.payable,
    outstanding: payLater ? quote.payable : 0,
    amount_paid: payLater ? 0 : quote.payable,
    gst_breakup: [],
    note,
  };
}

export function pendingBadgeCount(serverCount, snap) {
  const settles = snap?.settles || [];
  const charges = snap?.charges || [];
  const closingSettles = settles.filter((row) => row.closesBill !== false);
  const settledSales = new Set(closingSettles.map((row) => row.clientSaleId).filter(Boolean));
  const extra = charges.filter((row) => (
    isCreditCharge(row) && !row.orderId && !settledSales.has(row.clientSaleId)
  )).length;
  const collectedLocally = closingSettles.filter((row) => row.orderId).length;
  return Math.max(0, (Number(serverCount) || 0) + extra - collectedLocally);
}

export function adjustPhoneBalance(serverBalance, phone, snap) {
  const digits = phoneDigits(phone);
  const settles = (snap?.settles || []).filter((row) => phoneDigits(row.customerPhone) === digits);
  const settledSales = new Set(
    settles.filter((row) => row.closesBill !== false).map((row) => row.clientSaleId).filter(Boolean),
  );
  const extraCharges = (snap?.charges || [])
    .filter((row) => (
      isCreditCharge(row)
      && phoneDigits(row.customerPhone) === digits
      && !row.orderId
      && !settledSales.has(row.clientSaleId)
    ))
    .reduce((sum, row) => sum + estimateTotal(row), 0);
  const serverSettles = settles
    .filter((row) => row.orderId || row.closesBill === false)
    .reduce((sum, row) => sum + (Number(row.billAmount) || 0), 0);
  return roundMoney((Number(serverBalance) || 0) + extraCharges - serverSettles);
}

export function localDrawerByMethod(snap, istDate) {
  const settles = Array.isArray(snap) ? snap : (snap?.settles || []);
  const charges = Array.isArray(snap) ? [] : (snap?.charges || []);
  const totals = { CASH: 0, UPI: 0, CARD: 0 };
  (settles || []).forEach((row) => {
    if (istDate && istDay(row.settledAt) !== istDate) return;
    const method = String(row.paymentMethod || '').toUpperCase();
    if (!Object.prototype.hasOwnProperty.call(totals, method)) return;
    totals[method] = roundMoney(totals[method] + (Number(row.drawerAmount) || 0));
  });
  // Unsynced cash charges are counted when queued. A synced row is already in the server summary.
  charges.forEach((row) => {
    if (row.orderId || row.conflict) return;
    if (String(row.paymentMethod || '').toUpperCase() !== 'CASH') return;
    if (istDate && istDay(row.chargedAt) !== istDate) return;
    totals.CASH = roundMoney(totals.CASH + queuedBillAmount(row));
  });
  return totals;
}

function istDay(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function queueErrorMessage(err) {
  const code = err?.code || err?.message;
  return mapPosChargeError(err) || mapPayLaterError(err) || code || 'Could not sync this bill.';
}

export function createOfflineQueue(storage, {
  postSale = createPosSale,
  postSettle = settlePayLater,
} = {}) {
  async function listOps() {
    const rows = await storage.getAll(OPS);
    return (rows || []).slice().sort((a, b) => (a.queuedAt || 0) - (b.queuedAt || 0));
  }

  async function snapshot() {
    const ops = await listOps();
    const pending = await storage.get(META, 'pending');
    return {
      charges: ops.filter((row) => row.type === 'charge'),
      settles: ops.filter((row) => row.type === 'settle'),
      pendingItems: Array.isArray(pending?.items) ? pending.items : [],
      pendingSummary: pending?.summary || null,
    };
  }

  async function putOp(row) {
    await storage.put(OPS, row);
    emitQueue();
    return row;
  }

  async function enqueueCharge(input) {
    const clientSaleId = input.clientSaleId || newClientPaymentId();
    const existing = (await listOps()).find((row) => row.id === clientSaleId);
    const paymentMethod = String(input.paymentMethod || existing?.paymentMethod || 'PAY_LATER').toUpperCase();
    const priced = {
      items: input.items || existing?.items || [],
      discountAmount: input.discountAmount ?? existing?.discountAmount ?? 0,
    };
    const drawerAmount = input.drawerAmount != null
      ? roundMoney(input.drawerAmount)
      : (paymentMethod === 'CASH'
        ? payableForTender(estimateTotal(priced), 'CASH').payable
        : roundMoney(existing?.drawerAmount || 0));
    const row = {
      ...(existing || {}),
      id: clientSaleId,
      type: 'charge',
      queuedAt: existing?.queuedAt || Date.now(),
      clientSaleId,
      paymentMethod,
      drawerAmount,
      needsLot: input.needsLot != null ? Boolean(input.needsLot) : Boolean(existing?.needsLot),
      chargedAt: existing?.chargedAt || input.chargedAt || new Date().toISOString(),
      counter: input.counter,
      items: input.items || existing?.items || [],
      customerName: input.customerName ?? existing?.customerName ?? '',
      customerPhone: phoneDigits(input.customerPhone ?? existing?.customerPhone),
      addressLine1: input.addressLine1 ?? existing?.addressLine1 ?? '',
      addressLine2: input.addressLine2 ?? existing?.addressLine2 ?? '',
      block: input.block ?? existing?.block ?? '',
      flat: input.flat ?? existing?.flat ?? '',
      couponCode: input.couponCode ?? existing?.couponCode ?? null,
      discountAmount: input.discountAmount ?? existing?.discountAmount ?? 0,
      paidNow: input.paidNow ?? existing?.paidNow ?? null,
      paidNowMethod: input.paidNowMethod ?? existing?.paidNowMethod ?? null,
      clientPaymentId: input.clientPaymentId ?? existing?.clientPaymentId ?? null,
      paidNowAt: input.paidNowAt ?? existing?.paidNowAt ?? null,
      attempted: Boolean(input.attempted || existing?.attempted),
      errorCode: input.errorCode ?? null,
      errorMessage: input.errorMessage ?? null,
      errorItemId: input.errorItemId ?? null,
      orderId: existing?.orderId || null,
      displayId: existing?.displayId || null,
      receipt: existing?.receipt || null,
      conflict: existing?.conflict || false,
      cashierName: input.cashierName || existing?.cashierName || '',
      customerNumber: existing?.customerNumber || input.customerNumber || null,
    };
    await putOp(row);
    if (row.customerPhone) {
      await rememberPhone({
        phone: row.customerPhone,
        customerName: row.customerName,
        address: {
          customerName: row.customerName,
          addressLine1: row.addressLine1,
          addressLine2: row.addressLine2,
          block: row.block,
          flat: row.flat,
        },
      });
    }
    return row;
  }

  async function enqueueSettle(input) {
    const clientSettleId = input.clientSettleId || newClientPaymentId();
    const existing = (await listOps()).find((row) => row.id === clientSettleId);
    const row = {
      ...(existing || {}),
      id: clientSettleId,
      type: 'settle',
      queuedAt: existing?.queuedAt || Date.now(),
      clientSettleId,
      clientSaleId: input.clientSaleId || existing?.clientSaleId || null,
      orderId: input.orderId || existing?.orderId || null,
      paymentMethod: String(input.paymentMethod || existing?.paymentMethod || '').toUpperCase(),
      note: input.note ?? existing?.note ?? '',
      settledAt: existing?.settledAt || input.settledAt || new Date().toISOString(),
      customerPhone: phoneDigits(input.customerPhone ?? existing?.customerPhone),
      amount: input.amount != null && input.amount !== ''
        ? roundMoney(input.amount)
        : (existing?.amount ?? null),
      closesBill: input.closesBill != null
        ? Boolean(input.closesBill)
        : existing?.closesBill !== false,
      billAmount: roundMoney(input.billAmount ?? existing?.billAmount ?? 0),
      drawerAmount: roundMoney(input.drawerAmount ?? existing?.drawerAmount ?? 0),
      attempted: Boolean(input.attempted || existing?.attempted),
      errorCode: input.errorCode ?? null,
      errorMessage: input.errorMessage ?? null,
    };
    return putOp(row);
  }

  async function discardCharge(clientSaleId) {
    const ops = await listOps();
    const charge = ops.find((row) => row.type === 'charge' && row.clientSaleId === clientSaleId);
    if (!charge || !canDiscardCharge(charge)) return false;
    await storage.delete(OPS, charge.id);
    const settles = ops.filter((row) => row.type === 'settle' && row.clientSaleId === clientSaleId);
    await Promise.all(settles.map((row) => storage.delete(OPS, row.id)));
    emitQueue();
    return true;
  }

  async function updateCharge(clientSaleId, patch) {
    const ops = await listOps();
    const charge = ops.find((row) => row.type === 'charge' && row.clientSaleId === clientSaleId);
    if (!charge || charge.orderId || charge.conflict) return null;
    if (!canEditCharge(charge) && patch.items) return null;
    const next = {
      ...charge,
      ...patch,
      id: charge.id,
      type: 'charge',
      clientSaleId: charge.clientSaleId,
      chargedAt: charge.chargedAt,
      errorCode: patch.errorCode === undefined ? null : patch.errorCode,
      errorMessage: patch.errorMessage === undefined ? null : patch.errorMessage,
    };
    if (patch.items && String(next.paymentMethod || '').toUpperCase() === 'CASH') {
      next.drawerAmount = payableForTender(estimateTotal(next), 'CASH').payable;
    }
    return putOp(next);
  }

  async function rememberPhone({ phone, customerName, balance, openCount, addresses, address }) {
    const digits = phoneDigits(phone);
    if (digits.length !== 10) return null;
    const prev = await storage.get(PHONES, digits);
    const nextAddresses = Array.isArray(addresses)
      ? addresses
      : (prev?.addresses || []);
    const merged = address && (address.addressLine1 || address.addressLine2)
      ? [
        address,
        ...nextAddresses.filter((row) => (
          row.addressLine1 !== address.addressLine1
          || row.addressLine2 !== address.addressLine2
          || row.block !== address.block
          || row.flat !== address.flat
        )),
      ]
      : nextAddresses;
    const record = {
      phone: digits,
      customerName: customerName || prev?.customerName || null,
      balance: balance != null ? Number(balance) || 0 : (Number(prev?.balance) || 0),
      openCount: openCount != null ? Number(openCount) || 0 : (Number(prev?.openCount) || 0),
      addresses: merged,
      cachedAt: new Date().toISOString(),
    };
    await storage.put(PHONES, record);
    emitQueue();
    return record;
  }

  async function readPhone(phone) {
    const digits = phoneDigits(phone);
    if (digits.length !== 10) return null;
    return storage.get(PHONES, digits);
  }

  async function savePendingList(items, summary) {
    await storage.put(META, {
      key: 'pending',
      items: items || [],
      summary: summary || null,
      cachedAt: new Date().toISOString(),
    });
    emitQueue();
  }

  async function knownPhone(phone) {
    const book = await readPhone(phone);
    const snap = await snapshot();
    const digits = phoneDigits(phone);
    const charges = snap.charges.filter((row) => (
      isCreditCharge(row) && phoneDigits(row.customerPhone) === digits && !row.orderId
    ));
    return {
      phone: digits,
      customerName: book?.customerName || null,
      balance: adjustPhoneBalance(book?.balance || 0, digits, snap),
      openCount: Math.max(0, (Number(book?.openCount) || 0) + charges.length - snap.settles.filter((row) => (
        phoneDigits(row.customerPhone) === digits && row.orderId && row.closesBill !== false
      )).length),
      addresses: book?.addresses || [],
      lastKnown: true,
      hasBook: Boolean(book) || charges.length > 0,
    };
  }

  function chargeBody(row) {
    const method = String(row.paymentMethod || 'PAY_LATER').toUpperCase();
    const body = {
      items: (row.items || []).map((line) => {
        const item = { item_id: line.item_id, qty: line.qty };
        if (line.lot_id) item.lot_id = line.lot_id;
        return item;
      }),
      payment_method: method,
      counter: row.counter,
      discount_percent: 0,
      client_sale_id: row.clientSaleId,
      charged_at: row.chargedAt,
    };
    if (method === 'PAY_LATER') {
      if (row.couponCode) body.coupon_code = row.couponCode;
      body.customer_name = row.customerName;
      body.customer_phone = row.customerPhone;
      body.address_line1 = row.addressLine1;
      body.address_line2 = row.addressLine2;
      body.block = row.block;
      body.flat = row.flat;
      if (Number(row.paidNow) > 0) {
        body.paid_now = row.paidNow;
        body.paid_now_method = row.paidNowMethod;
        body.client_payment_id = row.clientPaymentId;
        body.settled_at = row.paidNowAt;
        body.paid_at = row.paidNowAt;
      }
    } else if (row.customerPhone) {
      body.customer_phone = row.customerPhone;
    }
    return body;
  }

  async function replay({ token } = {}) {
    if (!token) return { stopped: true, reason: 'no-token' };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { stopped: true, reason: 'offline' };
    }
    const ops = await listOps();
    const events = [];
    for (const op of ops) {
      if (op.type === 'charge') {
        if (op.orderId || op.conflict) continue;
        const outcome = await replayCharge(op, token);
        if (outcome.orderId) op.orderId = outcome.orderId;
        if (outcome.synced) {
          events.push({
            type: outcome.conflict ? 'conflict' : 'charge-synced',
            receipt: outcome.receipt,
            clientSaleId: outcome.clientSaleId,
            paymentMethod: outcome.paymentMethod,
            variance: outcome.variance || 0,
            collectedAmount: outcome.collectedAmount,
            serverTotal: outcome.serverTotal,
          });
        }
        if (outcome.stop) return { ...outcome, stopped: true, events };
      } else if (op.type === 'settle') {
        const charge = ops.find((row) => row.type === 'charge' && row.clientSaleId === op.clientSaleId);
        const orderId = op.orderId || charge?.orderId;
        if (!orderId) {
          return { stopped: true, reason: 'waiting-for-charge', id: op.clientSaleId, events };
        }
        const outcome = await replaySettle(op, orderId, token);
        if (outcome.synced || outcome.alreadyCollected) {
          events.push({
            type: outcome.alreadyCollected ? 'already-collected' : 'settle-synced',
            clientSettleId: outcome.clientSettleId,
          });
        }
        if (outcome.stop) return { ...outcome, stopped: true, events };
      }
    }
    return { stopped: false, events };
  }

  async function replayCharge(row, token) {
    const claimed = { ...row, attempted: true };
    await putOp(claimed);
    try {
      const data = await postSale(chargeBody(claimed), token);
      const receipt = data?.receipt || data?.sale || data || {};
      const sale = data?.sale || {};
      const orderId = receipt.order_id || sale.order_id || null;
      const replayed = data?.idempotent_replay === true;
      const conflict = replayed && !chargeLinesMatch(claimed.items, receipt.line_items || sale.line_items || []);
      const method = String(claimed.paymentMethod || 'PAY_LATER').toUpperCase();
      const collectedAmount = queuedBillAmount(claimed);
      const serverRaw = receipt.total ?? sale.total;
      const serverTotal = Number.isFinite(Number(serverRaw)) ? roundMoney(serverRaw) : collectedAmount;
      const variance = method === 'PAY_LATER' ? 0 : roundMoney(serverTotal - collectedAmount);
      await putOp({
        ...claimed,
        orderId,
        displayId: receipt.display_id || sale.display_id || null,
        receipt,
        conflict,
        variance,
        collectedAmount,
        serverTotal,
        errorCode: null,
        errorMessage: conflict
          ? 'The edit was not saved. The original bill is the one on the server.'
          : null,
      });
      return {
        stop: false,
        synced: true,
        conflict,
        receipt,
        orderId,
        clientSaleId: claimed.clientSaleId,
        paymentMethod: method,
        variance,
        collectedAmount,
        serverTotal,
      };
    } catch (err) {
      const code = err?.code || (!err?.status ? 'network' : err?.message) || 'network';
      await putOp({
        ...claimed,
        errorCode: code,
        errorMessage: queueErrorMessage(err),
        errorItemId: err?.body?.item_id || err?.body?.itemId || null,
      });
      return { stop: true, reason: code, id: claimed.clientSaleId };
    }
  }

  async function replaySettle(row, orderId, token) {
    const claimed = { ...row, attempted: true, orderId };
    await putOp(claimed);
    try {
      const settleBody = {
        payment_method: claimed.paymentMethod,
        note: claimed.note || undefined,
        client_payment_id: claimed.clientSettleId,
        client_settle_id: claimed.clientSettleId,
        settled_at: claimed.settledAt,
      };
      if (claimed.closesBill === false && claimed.amount != null) {
        settleBody.amount = claimed.amount;
      }
      const data = await postSettle(orderId, settleBody, token);
      await storage.delete(OPS, claimed.id);
      emitQueue();
      return {
        stop: false,
        synced: true,
        replay: data?.idempotent_replay === true,
        clientSettleId: claimed.clientSettleId,
      };
    } catch (err) {
      const code = err?.code || (!err?.status ? 'network' : err?.message) || 'network';
      if (code === 'already_settled' || code === 'already_paid') {
        await storage.delete(OPS, claimed.id);
        emitQueue();
        return { stop: false, alreadyCollected: true, clientSettleId: claimed.clientSettleId };
      }
      await putOp({
        ...claimed,
        errorCode: code,
        errorMessage: mapPayLaterError(err) || queueErrorMessage(err),
      });
      return { stop: true, reason: code, id: claimed.clientSettleId };
    }
  }

  return {
    snapshot,
    enqueueCharge,
    enqueueSettle,
    discardCharge,
    updateCharge,
    rememberPhone,
    readPhone,
    knownPhone,
    savePendingList,
    replay,
    estimateTotal,
  };
}

export const posOfflineQueue = createOfflineQueue(createNativeOfflineStorage());

let replayTimer = null;
let replayRunning = false;

export function startOfflineReplay({ getToken, onEvent } = {}) {
  const run = async () => {
    if (replayRunning) return;
    replayRunning = true;
    try {
      const token = typeof getToken === 'function' ? getToken() : null;
      const result = await posOfflineQueue.replay({ token });
      if (typeof onEvent === 'function') {
        (result?.events || []).forEach((event) => onEvent(event));
      }
    } catch {
      // Leave the rows. The next online/focus pass retries the same ids.
    } finally {
      replayRunning = false;
    }
  };

  const arm = () => {
    if (replayTimer) clearInterval(replayTimer);
    replayTimer = setInterval(() => {
      posOfflineQueue.snapshot().then((snap) => {
        const pending = snap.charges.some((row) => !row.orderId && !row.conflict)
          || snap.settles.length > 0;
        if (pending) run();
      }).catch(() => {});
    }, 15000);
  };

  arm();
  run();
  const unsubscribe = subscribeOfflineQueue(() => {
    if (!replayRunning) run();
  });
  const onOnline = () => run();
  const onFocus = () => run();
  window.addEventListener('online', onOnline);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);
  return () => {
    if (replayTimer) clearInterval(replayTimer);
    replayTimer = null;
    unsubscribe();
    window.removeEventListener('online', onOnline);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}

export function mergePayLaterRows({ serverItems = [], snap, tab = 'pending', query = '' }) {
  const settles = snap?.settles || [];
  const charges = snap?.charges || [];
  const settleFor = (record) => settles.find((row) => (
    (record.orderId && row.orderId === record.orderId)
    || (record.clientSaleId && row.clientSaleId === record.clientSaleId)
  ));

  const localRows = charges
    .filter((row) => isCreditCharge(row) && !row.orderId)
    .map((row) => localChargeRow(row, settleFor(row)));
  const serverRows = (serverItems || []).map((record) => {
    const settle = settleFor(record);
    if (!settle || record.status === 'COLLECTED') return record;
    return applyQueuedSettle(record, settle);
  });

  const seen = new Set(serverRows.map((row) => row.orderId).filter(Boolean));
  const extras = localRows.filter((row) => !seen.has(row.orderId));
  let rows = [...serverRows, ...extras];
  if (tab === 'pending') {
    rows = rows.filter((row) => row.status === 'PENDING' && !row.localSettlement);
  } else if (tab === 'collected') {
    rows = rows.filter((row) => row.status === 'COLLECTED');
  }
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => {
    const customer = row.customer || {};
    const hay = [
      customer.fullName, customer.phone, row.displayId, row.orderId, row.clientSaleId,
    ].join(' ').toLowerCase();
    return hay.includes(needle);
  });
}

function applyQueuedSettle(record, settle) {
  const partial = settle.closesBill === false;
  const dueNow = roundMoney(record.outstanding);
  const slice = partial
    ? roundMoney(settle.amount ?? settle.billAmount)
    : dueNow;
  const due = roundMoney(Math.max(0, dueNow - slice));
  const fully = !partial || due <= 0;
  return {
    ...record,
    status: fully ? 'COLLECTED' : 'PENDING',
    paymentStatus: fully ? 'COLLECTED' : 'PENDING',
    localSettlement: fully,
    waitingSync: true,
    syncError: settle.errorMessage || '',
    paidVia: settle.paymentMethod,
    paidAt: settle.settledAt,
    amountPaid: roundMoney((Number(record.amountPaid) || 0) + slice),
    outstanding: fully ? 0 : due,
  };
}

function localChargeRow(charge, settle) {
  const total = estimateTotal(charge);
  const partial = Boolean(settle) && settle.closesBill === false;
  const slice = partial ? roundMoney(settle.amount ?? settle.billAmount) : 0;
  const collected = Boolean(settle) && !partial;
  return {
    id: charge.clientSaleId,
    orderId: null,
    clientSaleId: charge.clientSaleId,
    displayId: localReference(charge.clientSaleId),
    createdAt: charge.chargedAt,
    amount: total,
    total,
    amountPaid: collected ? total : slice,
    outstanding: collected ? 0 : roundMoney(Math.max(0, total - slice)),
    status: collected ? 'COLLECTED' : 'PENDING',
    paymentStatus: collected ? 'COLLECTED' : 'PENDING',
    paymentMethod: 'PAY_LATER',
    counter: charge.counter,
    customer: {
      fullName: charge.customerName,
      phone: phoneDigits(charge.customerPhone),
      addressLine1: charge.addressLine1,
      addressLine2: charge.addressLine2,
      block: charge.block,
      flat: charge.flat,
    },
    localOnly: true,
    waitingSync: true,
    syncError: settle?.errorMessage || charge.errorMessage || '',
    errorCode: settle?.errorCode || charge.errorCode || '',
    canDiscard: canDiscardCharge(charge),
    canEdit: canEditCharge(charge),
    items: charge.items || [],
    couponCode: charge.couponCode || null,
    paidVia: settle?.paymentMethod || null,
    paidAt: settle?.settledAt || null,
    localSettlement: collected,
    receipt: charge.receipt || null,
  };
}

export function useBrowserOnline() {
  const [online, setOnline] = useState(() => (
    typeof navigator === 'undefined' || navigator.onLine !== false
  ));
  useEffect(() => {
    const setOn = () => setOnline(true);
    const setOff = () => setOnline(false);
    window.addEventListener('online', setOn);
    window.addEventListener('offline', setOff);
    return () => {
      window.removeEventListener('online', setOn);
      window.removeEventListener('offline', setOff);
    };
  }, []);
  return online;
}
